/**
 * Before / after routing diagram — the search engine as a lossy intermediary,
 * against a direct MCP connection.
 *
 * Accent #6366f1 validated against the dark surface (#0f172a).
 */

const ACCENT = '#6366f1';
const ACCENT_DIM = 'rgba(99,102,241,0.4)';
const MUTED_LINE = '#475569';
const STROKE = '#334155';
const SURFACE = 'rgba(30,41,59,0.5)';
const INK = '#e2e8f0';
const INK_MUTED = '#94a3b8';
const INK_FAINT = '#64748b';

function Node({
  x,
  y,
  label,
  sub,
  accent = false,
  dim = false,
}: {
  x: number;
  y: number;
  label: string;
  sub?: string;
  accent?: boolean;
  dim?: boolean;
}) {
  return (
    <g opacity={dim ? 0.55 : 1}>
      <rect
        x={x}
        y={y}
        width="150"
        height={sub ? 54 : 40}
        rx="10"
        fill={accent ? 'rgba(99,102,241,0.12)' : SURFACE}
        stroke={accent ? ACCENT : STROKE}
        strokeWidth={accent ? 1.5 : 1}
      />
      <text x={x + 75} y={y + (sub ? 24 : 25)} textAnchor="middle" fontSize="13" fontWeight="600" fill={INK}>
        {label}
      </text>
      {sub && (
        <text x={x + 75} y={y + 42} textAnchor="middle" fontSize="10.5" fill={INK_FAINT}>
          {sub}
        </text>
      )}
    </g>
  );
}

export function DirectConnectionDiagram() {
  return (
    <figure className="m-0">
      <svg
        viewBox="0 0 620 250"
        className="w-full h-auto"
        role="img"
        aria-label="Previously an AI agent reached a business through a search engine, which kept the user and leaked most of the traffic. With MCP the agent reaches the business directly."
      >
        <defs>
          <marker id="dc-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={ACCENT_DIM} />
          </marker>
          <marker id="dc-arrow-muted" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={MUTED_LINE} />
          </marker>
        </defs>

        {/* ---------- old route ---------- */}
        <text x="10" y="22" fontSize="10.5" fill={INK_FAINT} letterSpacing="0.08em">
          TODAY
        </text>

        <Node x={10} y={38} label="AI agent" dim />
        <path d="M 160 58 L 222 58" stroke={MUTED_LINE} strokeWidth="1.5" markerEnd="url(#dc-arrow-muted)" />
        <Node x={222} y={32} label="Search engine" sub="answers in place" dim />
        <path d="M 372 58 L 434 58" stroke={MUTED_LINE} strokeWidth="1.5" strokeDasharray="4 4" markerEnd="url(#dc-arrow-muted)" />
        <Node x={434} y={38} label="Your business" dim />

        <text x={403} y={48} textAnchor="middle" fontSize="10" fill="#f87171">
          most traffic
        </text>
        <text x={403} y={78} textAnchor="middle" fontSize="10" fill="#f87171">
          never arrives
        </text>

        {/* ---------- divider ---------- */}
        <line x1="10" y1="120" x2="610" y2="120" stroke={STROKE} strokeDasharray="3 5" />

        {/* ---------- new route ---------- */}
        <text x="10" y="152" fontSize="10.5" fill={ACCENT} letterSpacing="0.08em">
          WITH MCP
        </text>

        <Node x={10} y={172} label="AI agent" />
        <path d="M 160 192 L 428 192" stroke={ACCENT_DIM} strokeWidth="2" markerEnd="url(#dc-arrow)" />
        <Node x={434} y={166} label="Your business" sub="the authoritative source" accent />

        <text x={294} y={182} textAnchor="middle" fontSize="10.5" fill={INK_MUTED}>
          direct, machine-readable
        </text>
        <text x={294} y={210} textAnchor="middle" fontSize="10.5" fill={INK_MUTED}>
          and transactable
        </text>
      </svg>

      <figcaption className="text-[11px] text-slate-500 leading-relaxed mt-3">
        An assistant answering from a search index cites whatever it scraped. An assistant connected
        over MCP asks you — and can book, order or file a ticket in the same exchange.
      </figcaption>
    </figure>
  );
}
